import { Flex, Spinner } from '@chakra-ui/react'
import { Copy, Share } from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'
import { useClipboard } from '~/hooks/useClipboard'
import { Size } from '~/types/ThemeTypes'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import { ApiError, VoicemailInstance } from '@mangovoice/types'

interface VoicemailTranscriptSectionProps {
  voicemail?: VoicemailInstance
  voicemailError?: ApiError
  isVoicemailLoading?: boolean
  onVoicemailClick?: () => void
  isToolTip?: boolean
}

export const VoicemailTranscriptSection = ({
  voicemail,
  voicemailError,
  isVoicemailLoading,
  onVoicemailClick,
}: VoicemailTranscriptSectionProps) => {
  const { t } = useTranslation()
  const { toastError, toastSuccess } = useToastAlert()
  const { copyToClipboard } = useClipboard({
    onSuccess: () =>
      toastSuccess(t('callDetails.toast.voicemailTranscriptCopied')),
    onFailure: () =>
      toastError(t('callDetails.toast.voicemailTranscriptCopyFailed')),
  })

  const [isHovered, setIsHovered] = useState(false)

  const handleCopyVoicemailTranscript = async () => {
    try {
      if (voicemail?.transcription) {
        copyToClipboard(voicemail.transcription)
      } else {
        toastError(t('callDetails.toast.noVoicemailTranscript'))
      }
    } catch (error) {
      console.error('Failed to copy voicemail transcript:', error)
      toastError(t('callDetails.toast.voicemailTranscriptCopyError'))
    }
  }

  const renderVoicemailTranscript = () => {
    if (isVoicemailLoading) {
      return (
        <Flex align="center" mt="0.5rem">
          <Spinner size="sm" mr={2} />
          <Flex color="fg.default" {...textStyles[textStyleKeys.Body2]}>
            {t('callDetails.voicemail.loading')}
          </Flex>
        </Flex>
      )
    }

    if (voicemailError) {
      if (voicemailError.status === 403) {
        return (
          <Flex
            color="red.solid"
            {...textStyles[textStyleKeys.Body1]}
            mt="0.5rem"
          >
            {t('callDetails.voicemail.accessDenied')}
          </Flex>
        )
      } else if (voicemailError.status === 410) {
        return (
          <Flex
            color="fg.default"
            {...textStyles[textStyleKeys.Body1]}
            mt="0.5rem"
          >
            {t('callDetails.voicemail.deleted')}
          </Flex>
        )
      } else {
        return (
          <Flex
            color="fg.default"
            {...textStyles[textStyleKeys.Body1]}
            mt="0.5rem"
          >
            {t('callDetails.voicemail.notAvailable')}
          </Flex>
        )
      }
    }

    if (voicemail?.transcription) {
      return (
        <Flex
          color="fg.default"
          {...textStyles[textStyleKeys.Body1]}
          mt="0.5rem"
        >
          {voicemail.transcription}
        </Flex>
      )
    }

    return (
      <Flex color="fg.default" {...textStyles[textStyleKeys.Body1]} mt="0.5rem">
        {t('callDetails.voicemail.transcriptionNotAvailable')}
      </Flex>
    )
  }

  return (
    <Flex
      flexDirection="column"
      position="relative"
      width="full"
      alignSelf="stretch"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Flex
        color="fg.default"
        {...textStyles[textStyleKeys.Body1_Bold]}
        position="relative"
        width="full"
        alignSelf="stretch"
      >
        {t('callDetails.voicemail.title')}
        {voicemail && (
          <Flex
            alignItems="center"
            className="quick-action-buttons"
            display={isHovered ? 'flex' : 'none'}
            position="absolute"
            right="0"
            gap="0.5rem"
          >
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              size={Size.sm}
              actionIcon={Copy}
              onClick={handleCopyVoicemailTranscript}
              tooltipText={t('callDetails.tooltip.copyToClipboard')}
            />
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              size={Size.sm}
              actionIcon={Share}
              onClick={onVoicemailClick ?? (() => {})}
              tooltipText={t('callDetails.tooltip.goToVoicemail')}
            />
          </Flex>
        )}
      </Flex>

      {renderVoicemailTranscript()}
    </Flex>
  )
}
